import { ref } from 'vue';
import { resolveOrderOutcome } from '@/utils/trade-order';
import {
  isMmWakeMatchingBackendError,
  isPmMarketNoOppositeDepthError,
  needsMmWakeMatchingHint,
} from '@/domain/market/mm-wake-matching-hint';

/** App 壳：无流动性 / 唤醒做市 → 30–60 秒匹配提示（需点确定） */
export const mmWakeMatchingPromptOpen = ref(false);

/** @type {Array<() => void>} */
const dismissCallbacks = [];

/**
 * @param {{ onDone?: () => void }} [opts]
 */
export function openMmWakeMatchingPrompt(opts = {}) {
  if (typeof opts.onDone === 'function') {
    dismissCallbacks.push(opts.onDone);
  }
  mmWakeMatchingPromptOpen.value = true;
}

export function dismissMmWakeMatchingPrompt() {
  mmWakeMatchingPromptOpen.value = false;
  const cbs = dismissCallbacks.splice(0, dismissCallbacks.length);
  cbs.forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.warn('[mm-wake-matching] dismiss callback failed', err);
    }
  });
}

/**
 * 按当前下单 outcome 探测盘口是否无真实做市流动性（分钟盘 / 二元 / 体育 / 多元共用）。
 * @param {object} args
 * @param {(symbol: string, params: object) => Promise<object>} args.fetchDepth
 * @param {string} args.symbol
 * @param {object} args.orderPayload
 * @param {string} [args.marketKind]
 * @returns {Promise<boolean>}
 */
export async function probeTradedOutcomeNeedsMmWake({
  fetchDepth,
  symbol,
  orderPayload,
  marketKind,
}) {
  const sym = String(symbol ?? '').trim();
  if (!sym || typeof fetchDepth !== 'function') return false;
  try {
    const outcome = resolveOrderOutcome(
      orderPayload,
      orderPayload?.outcomes,
      orderPayload?.marketKind ?? marketKind
    );
    const depth = await fetchDepth(sym, { outcome, level: 1 });
    return needsMmWakeMatchingHint(depth);
  } catch {
    return false;
  }
}

/**
 * 下单失败：后端「暂无对手盘 / 唤醒做市」文案 → 前端弹窗（接管后勿再展示后端原文）。
 * @returns {boolean}
 */
export function maybePromptMmWakeMatchingFromError(err) {
  if (!isMmWakeMatchingBackendError(err)) return false;
  openMmWakeMatchingPrompt();
  return true;
}

/**
 * 下单失败兜底：业务码 400 且当前 outcome depth 无真实流动性 → 同款弹窗。
 * @returns {Promise<boolean>}
 */
export async function maybePromptMmWakeMatchingFromTradeError(err, ctx = {}) {
  if (isPmMarketNoOppositeDepthError(err)) return false;
  if (maybePromptMmWakeMatchingFromError(err)) return true;
  const code = err?.code ?? err?.response?.data?.code;
  if (!(code === 400 || String(code) === '400')) return false;
  const needs = await probeTradedOutcomeNeedsMmWake(ctx);
  if (!needs) return false;
  openMmWakeMatchingPrompt();
  return true;
}
